import { getAllNodesOfType } from "./calcAstUtils.js";
import { parseCalcExpression } from "./CalcParser.js";
import { columnNameNeedsBackticks } from "./moreCalcTypes.js";

/** Takes an unparsed calc expression and updates all column name references in the formula of `oldName` to `newName` */
export function updateCalcColumnReferences(
  expr: string,
  oldName: string,
  newName: string,
): string {
  let resultExpr = expr;
  let offset = 0;

  const alwaysNeedsBackticks = columnNameNeedsBackticks(newName);

  const ast = parseCalcExpression(resultExpr);
  const allColRefs = getAllNodesOfType(ast, "column");
  const colRefs = allColRefs.filter((c) => c.name === oldName);

  for (const colRef of colRefs) {
    const start = colRef.selfStart + offset;
    const end = colRef.selfEnd + offset;

    const origText = resultExpr.slice(start, end);

    const needsBackticks = alwaysNeedsBackticks || origText.startsWith("`");
    const newCol = needsBackticks ? `\`${newName}\`` : newName;

    resultExpr = resultExpr.slice(0, start) + newCol + resultExpr.slice(end);
    offset += newCol.length - origText.length;
  }

  return resultExpr;
}

/**
 * Takes an unparsed calc expression and updates all parameter references in the formula of `oldName` to `newName`.
 * Returns the new expression if changes were made, or `undefined` if no changes were made.
 */
export function updateCalcParameterReferences(
  expr: string,
  oldName: string,
  newName: string,
): string | undefined {
  const newParam = `{{ ${newName} }}`;

  let resultExpr = expr;
  let offset = 0;

  const ast = parseCalcExpression(resultExpr);
  const allParamRefs = getAllNodesOfType(ast, "parameterReference");
  const paramRefs = allParamRefs.filter((c) => c.name === oldName);

  if (paramRefs.length === 0) return undefined;

  for (const paramRef of paramRefs) {
    const start = paramRef.selfStart + offset;
    const end = paramRef.selfEnd + offset;

    const origText = resultExpr.slice(start, end);

    resultExpr = resultExpr.slice(0, start) + newParam + resultExpr.slice(end);
    offset += newParam.length - origText.length;
  }

  return resultExpr;
}
